<template>
  <div class="logwork-timeline-action d-flex align-items-center flex-wrap">
    <c-button class="mr-2" color="primary" @click="onSubmit">{{
      submitLabel
    }}</c-button>
    <c-button class="mr-2" color="primary" @click="onLogWork">
      <c-icon name="cil-alarm" class="mr-1" /> {{ logWorkLabel }}
    </c-button>

    <div class="daterange-input mr-2">
      <c-icon name="cil-calendar" class="daterange-icon" />
      <a-range-picker
        v-model="internalValue"
        :format="format"
        :disabled-date="disabledDate"
        @change="onRangeChange"
        :range-separator="'-'"
        class="ant-range-picker-inline"
      />
    </div>

    <select
      class="form-control w-auto mr-2"
      v-model="view"
      @change="onViewChange"
    >
      <option value="day">{{ dayLabel }}</option>
      <option value="week">{{ weekLabel }}</option>
      <option value="month">{{ monthLabel }}</option>
    </select>

    <c-button class="btn-export" color="light" @click="onExport">
      {{ exportLabel }}
    </c-button>
  </div>
</template>

<script>
import moment from "moment";
export default {
  name: "LogWorkTimelineAction",
  props: {
    action: { type: Object, default: () => ({}) },
    initialView: { type: String, default: "week" },
    initialDateRange: { type: Array, default: null }
  },
  data() {
    return {
      internalValue: this.initialDateRange || null,
      format: "DD/MM/YYYY",
      view: this.initialView || "week"
    };
  },
  watch: {
    initialView: {
      handler(newVal) {
        if (newVal && newVal !== this.view) {
          this.view = newVal;
        }
      },
      immediate: true
    },
    initialDateRange: {
      handler(newVal) {
        if (
          newVal &&
          JSON.stringify(newVal) !== JSON.stringify(this.internalValue)
        ) {
          this.internalValue = newVal;
        }
      },
      immediate: true
    }
  },
  mounted() {
    // default to current week
    if (!this.internalValue) {
      const start = moment().startOf("isoWeek");
      let end = moment().endOf("isoWeek");
      const today = moment();
      if (end.isAfter(today, "day")) end = today.clone();
      this.internalValue = [start, end];
      // emit initial date-change as timeline expects
      this.$emit(
        "date-change",
        `${start.format("DD/MM/YYYY")}-${end.format("DD/MM/YYYY")}`
      );
    }
  },
  computed: {
    displayDate() {
      if (!this.internalValue || !this.internalValue.length)
        return this.$t("commons.choose_date") || "Choose date";
      const s = this.internalValue[0].format("DD/MM/YYYY");
      const e = this.internalValue[1].format("DD/MM/YYYY");
      return `${s}-${e}`;
    },
    currentState() {
      return {
        dateRange: this.displayDate,
        view: this.view,
        hasValidDateRange: this.internalValue && this.internalValue.length === 2
      };
    },
    submitLabel() {
      return this.$te("label.button.submit")
        ? this.$t("label.button.submit")
        : "Submit";
    },
    logWorkLabel() {
      return this.$te("label.button.log_work")
        ? this.$t("label.button.log_work")
        : "Log Work";
    },
    exportLabel() {
      return this.$te("label.button.export_excel")
        ? this.$t("label.button.export_excel")
        : "Export Excel";
    },
    dayLabel() {
      return this.$te("label.timegroup.day")
        ? this.$t("label.timegroup.day")
        : "Day";
    },
    weekLabel() {
      return this.$te("label.timegroup.week")
        ? this.$t("label.timegroup.week")
        : "Week";
    },
    monthLabel() {
      return this.$te("label.timegroup.month")
        ? this.$t("label.timegroup.month")
        : "Month";
    }
  },
  methods: {
    onSubmit() {
      this.$emit("submit");
    },
    onLogWork() {
      this.$emit("log-work");
    },
    onExport() {
      this.$emit("export-excel");
    },
    onRangeChange(val) {
      if (!val || val.length === 0) {
        this.$emit("date-change", "");
        return;
      }

      const start = val[0];
      const end = val[1];

      // Validate date range
      if (!start || !end || start.isAfter(end)) {
        console.warn("Invalid date range selected");
        return;
      }

      // Format dates for emission
      const startStr = start.format("DD/MM/YYYY");
      const endStr = end.format("DD/MM/YYYY");

      this.$emit("date-change", `${startStr}-${endStr}`);
    },
    disabledDate(current) {
      return current && current.isAfter(moment(), "day");
    },
    onViewChange() {
      this.$emit("view-change", this.view);
    },
    resetToDefault() {
      // Reset to current week
      const start = moment().startOf("isoWeek");
      let end = moment().endOf("isoWeek");
      const today = moment();
      if (end.isAfter(today, "day")) end = today.clone();

      this.internalValue = [start, end];
      this.view = "week";

      // Emit changes
      this.$emit(
        "date-change",
        `${start.format("DD/MM/YYYY")}-${end.format("DD/MM/YYYY")}`
      );
      this.$emit("view-change", "week");
    }
  }
};
</script>

<style scoped>
.logwork-timeline-action {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.daterange-input .ant-range-picker-inline .ant-picker {
  min-height: 40px;
  border-radius: 12px;
  min-width: 320px;
}
.date-display {
  display: flex;
  align-items: center;
  gap: 8px;
}
.daterange-input {
  position: relative;
  display: inline-block;
}
.daterange-input .daterange-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  color: #66788a;
  width: 20px;
  height: 20px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  border: 0;
  pointer-events: none;
}
.daterange-input .ant-picker {
  padding-left: 36px;
}
.daterange-input .ant-picker-input input {
  padding-left: 8px;
}
.daterange-input .ant-picker .ant-picker-range-separator,
.daterange-input .ant-picker .ant-picker-range-arrow {
  padding: 0;
  margin: 0;
}
.daterange-input .ant-picker .ant-calendar-range-picker-input {
  width: 40%;
}
.btn-export {
  background: #fff;
  border: 1px solid #e6e9ee;
}
.logwork-timeline-action .btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 20px;
  font-size: 14px;
}
.logwork-timeline-action c-button,
.logwork-timeline-action .c-button {
  height: 40px;
  padding: 0 16px;
  border-radius: 20px;
}
.logwork-timeline-action select.form-control:focus {
  box-shadow: none;
  outline: none;
}
.logwork-timeline-action .ant-picker {
  box-shadow: none;
  outline: none;
}
</style>
